import React, { ReactNode } from 'react';
import { useResourceLoading } from '@api/utils/ApiLoadingStore';
import { useResourceError } from '@api/utils/ApiMessagesStore';
import { Resource } from '@api/resources';
import { Loader } from './common/Loader';
import { ErrorAlert } from './common/ErrorAlert';
import { RootState, useAppSelector } from '../store';
import { FetchState } from '../store/fetchState';

export interface ShowResourceContentProps {
  /**
   * Single resource or array of resources to monitor
   */
  resource?: Resource | Resource[];

  /**
   * Optional Redux FetchState selector(s) (e.g., for "new" forms or legacy state management)
   * Can be a single selector or an array of selectors
   */
  fetchStateSelector?: ((state: RootState) => FetchState) | ((state: RootState) => FetchState)[];

  /**
   * Content to render when not loading and no errors
   */
  children: ReactNode;

  /**
   * Whether to clear errors when component unmounts (default: false)
   */
  clearErrorOnExit?: boolean;
}

/**
 * Component that automatically handles loading and error states for API resources.
 * Shows a loader while any resource is loading, an error alert if any resource has errors,
 * and renders children otherwise.
 *
 * @example
 * // Single resource
 * <ShowResourceContent resource={resources.activity}>
 *   <ActivityForm />
 * </ShowResourceContent>
 *
 * @example
 * // Multiple resources
 * <ShowResourceContent resource={[resources.activity, resources.documents]}>
 *   <ActivityForm />
 * </ShowResourceContent>
 *
 * @example
 * // With Redux FetchState selector
 * <ShowResourceContent fetchStateSelector={selectActivityState}>
 *   <ActivityForm />
 * </ShowResourceContent>
 *
 * @example
 * // With multiple Redux FetchState selectors
 * <ShowResourceContent fetchStateSelector={[selectActivityState, selectCompanyChecksState]}>
 *   <ActivityForm />
 * </ShowResourceContent>
 */
export const ShowResourceContent: React.FC<ShowResourceContentProps> = ({
  resource,
  fetchStateSelector,
  children,
  clearErrorOnExit = false,
}) => {
  const isLoading = useResourceLoading(resource);
  const errors = useResourceError(resource, clearErrorOnExit);

  // Handle single or multiple selectors
  const selectors = Array.isArray(fetchStateSelector) ? fetchStateSelector : fetchStateSelector ? [fetchStateSelector] : [];

  // Evaluate every selector inside a single hook call — calling a hook per item in .map()
  // violates the rules of hooks. Returning primitives keeps the selector output stable.
  const isAnyLoading = useAppSelector((state) => selectors.some((selector) => selector(state) === FetchState.Loading));
  const hasAnyError = useAppSelector((state) => selectors.some((selector) => selector(state) === FetchState.Error));

  if (isLoading || isAnyLoading) {
    return <Loader />;
  }

  if (errors.length > 0 || hasAnyError) {
    // Resource errors carry an already-translated message; the redux fetchState path does not.
    const message = errors.length > 0 ? errors.map((err, index) => <div key={index}>{err}</div>) : undefined;
    return <ErrorAlert message={message} />;
  }

  return <>{children}</>;
};
